import { motion } from "framer-motion";
import { ExternalLink, Instagram } from "lucide-react";

const team = [
  {
    id: "tournoud",
    name: "Dr Mirabelle Tournoud",
    role: "Chirurgien-Dentiste — Associée",
    practice: "Dentisterie omnipratique",
    specialty: "Aligneurs invisibles pour adultes",
    duo: "Son binôme : Tatiana, assistante dentaire",
    email: "docteur.tournoud@gmail.com",
    image:
      "https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/895t7ld9_PSTK4175.JPG",
    doctolib:
      "https://www.doctolib.fr/dentiste/les-pennes-mirabeau/mirabelle-tournoud?pid=practice-150570&utm_campaign=google-maps&utm_content=marseille&utm_medium=organic&utm_source=google&utm_term=dentiste",
  },
  {
    id: "vermot",
    name: "Dr Jeremy Vermot",
    role: "Chirurgien-Dentiste — Associé",
    practice: "Dentisterie omnipratique",
    specialty: "Chirurgie implantaire et pré-implantaire",
    duo: "Son binôme : Audrey, assistante dentaire",
    email: "dr.vermotjeremy@gmail.com",
    instagram: {
      handle: "@dr.vermotjeremy",
      url: "https://www.instagram.com/dr.vermotjeremy/",
    },
    image:
      "https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/llnnxz4y_PSTK4559.JPG",
    doctolib:
      "https://www.doctolib.fr/dentiste/marseille/jeremy-vermot-9d74a0d9-6231-488c-a5ee-e05148da76b5",
  },
  {
    id: "apelbaum",
    name: "Dr Léo Apelbaum",
    role: "Chirurgien-Dentiste — Collaborateur",
    specialty: "Dentisterie générale & soins",
    duo: "Son binôme : Noémie, assistante dentaire",
    email: "drapelbauleo@gmail.com",
    image:
      "https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/kvd3vwqy_IMG_0060.JPG",
    doctolib:
      "https://www.doctolib.fr/dentiste/les-pennes-mirabeau/leo-apelbaum-les-pennes-mirabeau?pid=practice-150570&phs=true&page=1&index=1",
  },
];

export const Team = () => {
  return (
    <section
      id="equipe"
      data-testid="team-section"
      className="py-20 md:py-32 relative"
    >
      <div className="container-xl">
        <div className="max-w-2xl mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline">Notre Équipe</span>
            <h2 className="h2 text-4xl sm:text-5xl lg:text-6xl mt-5">
              Des praticiens experts,
              <br />
              <span className="italic text-[#BE6C42] font-light">
                dévoués à votre sourire.
              </span>
            </h2>
            <p className="body-text text-base sm:text-lg mt-6">
              Trois chirurgiens-dentistes passionnés, chacun avec son domaine
              d&apos;excellence, réunis pour vous offrir des soins d&apos;une
              qualité irréprochable.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {team.map((member, idx) => (
            <motion.article
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: idx * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="card-premium group flex flex-col"
              data-testid={`doctor-card-${member.id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="card-image absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D241E]/50 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm rounded-full px-3.5 py-1 text-[11px] tracking-wider uppercase text-[#BE6C42] font-semibold">
                  {member.id === "apelbaum" ? "Collaborateur" : "Associé"}
                </div>
              </div>

              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif text-2xl lg:text-[26px] text-[#2D241E] leading-tight">
                  {member.name}
                </h3>
                <div className="mt-1 text-sm text-[#6A584E]">{member.role}</div>

                <div className="mt-5 pt-5 border-t border-[#E8DED5]">
                  {member.practice && (
                    <>
                      <div className="text-xs uppercase tracking-widest text-[#6A584E] font-semibold mb-1">
                        Pratique
                      </div>
                      <p className="text-[15px] text-[#2D241E] leading-snug mb-4">
                        {member.practice}
                      </p>
                    </>
                  )}
                  <div className="text-xs uppercase tracking-widest text-[#BE6C42] font-semibold mb-2">
                    {member.practice ? "+ Spécialité" : "Spécialité"}
                  </div>
                  <p className="text-[15px] text-[#2D241E] font-medium leading-snug">
                    {member.specialty}
                  </p>
                  {member.duo && (
                    <p className="mt-3 text-[13px] text-[#6A584E] italic leading-snug">
                      {member.duo}
                    </p>
                  )}
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="mt-3 inline-block text-[13px] text-[#BE6C42] hover:text-[#A65A34] transition-colors break-all"
                      data-testid={`doctor-email-${member.id}`}
                    >
                      {member.email}
                    </a>
                  )}
                  {member.instagram && (
                    <a
                      href={member.instagram.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-[13px] text-[#BE6C42] hover:text-[#A65A34] transition-colors"
                      data-testid={`doctor-instagram-${member.id}`}
                    >
                      <Instagram size={14} strokeWidth={1.8} />
                      {member.instagram.handle}
                    </a>
                  )}
                </div>

                <a
                  href={member.doctolib}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-7 justify-center"
                  data-testid={`doctolib-booking-${member.id}`}
                >
                  Prendre RDV sur Doctolib
                  <ExternalLink size={16} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
